import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useReveal from "@/hooks/useReveal";

const COLLECTIONS = [
  {
    id: "shades",
    chapter: "Chapter 01",
    title: "Shades",
    tagline: "The Africa Frame™ — Gold Edition.",
    body: "Sculpted statement piece. Africa-shaped left, hexagonal right. Edition 001 — 300 pieces worldwide.",
    image:
      "https://customer-assets.emergentagent.com/job_afrqa-luxury/artifacts/0esx8z7g_fcda3137-3915-43e7-b3f9-6b5055ad7dbd.JPG",
    bg: "bg-[#0c0c0c]",
    badge: "Active · $99",
    cta: { label: "Enter Collection", to: "/product/africa-frame" },
    accent: "text-[#d4af37]",
  },
  {
    id: "watches",
    chapter: "Chapter 02",
    title: "Watches",
    tagline: "The Africa Timepiece™.",
    body: "The first timepiece sculpted in the silhouette of the continent. Edition 002 — coming 2026.",
    image:
      "https://customer-assets.emergentagent.com/job_afrqa-luxury/artifacts/yrumj0o4_Afrqa%20watch.PNG",
    bg: "bg-[#e6d5b8]",
    badge: "Coming Soon",
    cta: { label: "Notify Me on Launch", to: "/#watch" },
    accent: "text-[#d4af37]",
  },
  {
    id: "future-systems",
    chapter: "Chapter 03",
    title: "Future Systems",
    tagline: "Adaptive Vision.",
    body: "A new wearable language. Phase II of AFRQA™ — in studio.",
    image: null,
    bg: "bg-[#070707]",
    badge: "Phase II · Loading",
    cta: { label: "Read the Manifesto", to: "/vision" },
    accent: "text-[#d4af37]",
  },
  {
    id: "upcoming",
    chapter: "Chapter 04",
    title: "Upcoming Drops",
    tagline: "The Archive Expands.",
    body: "Editions in development. First invitations sent through The List.",
    image: null,
    bg: "bg-[#070707]",
    badge: "By Invitation",
    cta: { label: "Join the List", to: "/#packaging" },
    accent: "text-[#d4af37]",
  },
];

function ChromePlaceholder({ id }) {
  // Atmospheric placeholder for collections without product photography yet.
  // Pure CSS — chrome / matte / radial light — no AI generation, no stock.
  const id1 = `g1-${id}`;
  const id2 = `g2-${id}`;
  return (
    <div className="w-full h-full relative overflow-hidden">
      <svg
        viewBox="0 0 400 500"
        preserveAspectRatio="xMidYMid slice"
        className="w-full h-full"
        aria-hidden
      >
        <defs>
          <radialGradient id={id1} cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#3a3a36" />
            <stop offset="55%" stopColor="#141414" />
            <stop offset="100%" stopColor="#050505" />
          </radialGradient>
          <linearGradient id={id2} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#d4af37" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#d4af37" stopOpacity="0" />
          </linearGradient>
        </defs>
        <rect width="400" height="500" fill={`url(#${id1})`} />
        {/* Subtle scanlines */}
        {Array.from({ length: 8 }).map((_, i) => (
          <line
            key={i}
            x1="0"
            x2="400"
            y1={i * 62 + 30}
            y2={i * 62 + 30}
            stroke="#ffffff"
            strokeOpacity="0.025"
          />
        ))}
        {/* Diagonal chrome streak */}
        <polygon
          points="0,500 250,0 400,0 400,80 130,500"
          fill={`url(#${id2})`}
        />
        {/* AFRQA wordmark, very subtle */}
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#d4af37"
          fillOpacity="0.45"
          fontFamily="Outfit, sans-serif"
          fontSize="38"
          letterSpacing="14"
        >
          AFRQA
        </text>
        <text
          x="50%"
          y="58%"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#cfcfca"
          fillOpacity="0.55"
          fontFamily="Cormorant Garamond, serif"
          fontStyle="italic"
          fontSize="22"
        >
          phase II
        </text>
      </svg>
    </div>
  );
}

function CollectionCard({ entry }) {
  const r = useReveal();
  return (
    <article
      ref={r.ref}
      className={`reveal ${r.inView ? "in-view" : ""} group`}
      data-testid={`collection-${entry.id}`}
    >
      <div
        className={`zoom-wrap prototype-photo relative border border-[#1f1f1f] aspect-[4/5] overflow-hidden ${entry.bg}`}
      >
        {entry.image ? (
          <img
            src={entry.image}
            alt={entry.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <ChromePlaceholder id={entry.id} />
        )}
        <span
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.15) 40%, transparent 75%)",
          }}
        />

        <div className="absolute top-5 left-5 right-5 flex items-center justify-between font-label text-[#cfcfca]">
          <span>{entry.chapter}</span>
          <span className={entry.accent}>{entry.badge}</span>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <h3 className="font-display text-white text-[clamp(1.8rem,3.2vw,2.8rem)] leading-[1.03]">
            {entry.title}
          </h3>
          <p className="mt-2 font-editorial italic text-[#e6d5b8] text-lg md:text-xl">
            {entry.tagline}
          </p>
          <p className="mt-4 text-[#cfcfca] text-sm md:text-base font-light max-w-md leading-relaxed">
            {entry.body}
          </p>
          <Link
            to={entry.cta.to}
            data-testid={`collection-cta-${entry.id}`}
            className="mt-6 inline-flex items-center gap-3 font-label text-[#d4af37] group-hover:text-white transition-colors"
          >
            <span className="w-6 h-px bg-current" />
            {entry.cta.label}
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function CollectionsPage() {
  const r1 = useReveal();

  return (
    <div data-testid="collections-page" className="bg-[#0a0a0a] text-white">
      <Header />
      <main className="pt-28 md:pt-36 pb-24">
        <section className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div
            ref={r1.ref}
            className={`reveal ${r1.inView ? "in-view" : ""} max-w-3xl`}
          >
            <span className="font-label text-[#d4af37]">— The Collections —</span>
            <h1 className="mt-5 font-display text-[clamp(2.6rem,6vw,5.4rem)] leading-[0.96] tracking-[-0.025em]">
              Chapters of{" "}
              <span className="font-editorial italic gold-text-gradient">
                a future house.
              </span>
            </h1>
            <p className="mt-8 max-w-xl text-[#cfcfca] text-lg leading-relaxed font-light">
              Each AFRQA™ collection is a chapter. We move slowly. We make
              less.
            </p>
          </div>
        </section>

        <section className="mt-16 md:mt-20 max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {COLLECTIONS.map((c) => (
              <CollectionCard key={c.id} entry={c} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
